/*  MUI  */
import {
    DeleteOutlined,
    ExpandMore,
    RestoreFromTrash
} from '@mui/icons-material';
import {
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { AdminView } from 'application/Shared/Components/AdminView';
import { PageTitle } from 'application/Shared/Components/App';
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { UserBubbles } from 'application/Shared/Components/Avatars';
import { AdminBackButton } from 'application/Shared/Components/Buttons/BackButton';
import {
    BusinessOwnerPicker,
    LifecyclePicker, // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    TagPicker // TODO: SAAS-1831 - Either remove this or re-implement it with Global Tags
} from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Notify } from 'application/Shared/Helpers';
import Core from 'application/Shared/Helpers/Functions';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import { Application } from 'objects/Application';
import BusinessOwnerFunctions from 'objects/functions/BusinessOwner';

/*  React  */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

const BUSINESS_OWNER_CONFIG = 'business_owner';
const LIFECYCLE_STAGE_CONFIG = 'status';
const TAGS_CONFIG = 'tags';

const RenderRowBusinessOwner = ({ configuration, row, valueChange }) => (
    <Application data={row.application} nameHasLink>
        <TableCell>
            <ApplicationNameLinkCell nameNoTruncate withCategory />
        </TableCell>
        <TableCell align="center">{row.userCount}</TableCell>
        <TableCell align="center">
            <>
                {React.cloneElement(configuration.component, {
                    managedByPartner: row.managedByPartner,
                    noBusinessOwnerRequired: row.noBusinessOwnerRequired,
                    onChange: (value) => {
                        valueChange(row, value);
                    },
                    value: row[configuration.field]
                })}
            </>
        </TableCell>
    </Application>
);

const RenderRowStatus = ({ configuration, row, valueChange }) => (
    <Application data={row.application} nameHasLink>
        <TableCell>
            <ApplicationNameLinkCell nameNoTruncate withCategory />
        </TableCell>
        <TableCell align="center">{row.userCount}</TableCell>
        <TableCell align="center">
            <>
                {React.cloneElement(configuration.component, {
                    onChange: (value) => {
                        valueChange(row, value);
                    },
                    value: row[configuration.field]
                })}
            </>
        </TableCell>
    </Application>
);

const RenderRowTags = ({ configuration, row, valueChange }) => (
    <Application data={row.application} nameHasLink>
        <TableCell>
            <ApplicationNameLinkCell nameNoTruncate withCategory />
        </TableCell>
        <TableCell align="center">{row.userCount}</TableCell>
        <TableCell align="center">
            <>
                {React.cloneElement(configuration.component, {
                    onChange: (value) => {
                        valueChange(row, value);
                    },
                    value:
                        row[TAGS_CONFIG].length > 0 ? row[TAGS_CONFIG] : ['0']
                })}
            </>
        </TableCell>
    </Application>
);

const headersDelete = ({ t, text }) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.application')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        width: '40%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.users')}</strong>,
        width: '40%'
    },
    {
        align: 'right',
        field: 'dynamic',
        renderHeader: <strong>{text}</strong>,
        width: '20%'
    }
];

const headers = ({ field, t, text }) => [
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{t('Common.application')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'application.name',
        width: '40%'
    },

    {
        align: 'center',
        field: 'userCount',
        renderHeader: <strong>{t('Common.user_count')}</strong>,
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'userCount',
        width: '20%'
    },
    {
        align: 'left',
        field: 'dynamic',
        renderHeader: <strong>{text}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: field,
        width: '40%'
    }
];

const parameters = {
    includeApplication: true,
    includeApplicationLinkTags: true,
    includeUserCount: true
};

const parametersDelete = {
    applicationId: '*',
    classified: true,
    includeApplication: true,
    includeUser: true,
    size: -1
};

const searchFields = [
    'application.category',
    'application.id',
    'application.name',
    'application.subCategory'
];

const togglesBusinessOwner = ({ t, text }) => [
    {
        defaultEnabled: true,
        key: 'exclude',
        parameter: {
            field: 'businessOwnerPlatformUserId',
            value: null
        },
        toggleText: `${t('BulkEditor.exclude_section_one')}${text}${t(
            'BulkEditor.exclude_section_two'
        )}`
    },
    {
        defaultEnabled: true,
        key: 'exclude_no_business_owner_required',
        parameter: {
            field: 'noBusinessOwnerRequired',
            value: false
        },
        toggleText: `${t('BulkEditor.exclude_no_business_owner_required')}`
    },
    {
        defaultEnabled: true,
        key: 'exclude_unclassified_applications',
        parameter: {
            field: 'classified',
            value: true
        },
        toggleText: `${t('BulkEditor.exclude_unclassified_applications')}`
    }
];

const togglesDelete = ({ t }) => [
    {
        defaultEnabled: true,
        key: 'removed',
        parameter: {
            field: 'includeRemoved',
            value: 'true'
        },
        toggleText: t('BulkEditor.includeRemovedApplications')
    }
];

const togglesStatus = ({ t, text }) => [
    {
        defaultEnabled: true,
        key: 'exclude',
        parameter: {
            field: 'status',
            value: 'not_configured'
        },
        toggleText: `${t('BulkEditor.exclude_section_one')}${text}${t(
            'BulkEditor.exclude_section_two'
        )}`
    },
    {
        defaultEnabled: true,
        key: 'exclude_unclassified_applications',
        parameter: {
            field: 'classified',
            value: true
        },
        toggleText: `${t('BulkEditor.exclude_unclassified_applications')}`
    }
];

const BusinessOwnerTable = ({ configuration, valueChange }) => {
    const { t } = useTranslation();

    const { text } = configuration;

    const businessOwnerHeaders = useMemo(
        () =>
            headers({
                field: BUSINESS_OWNER_CONFIG,
                t,
                text
            }),
        [t, text]
    );

    return (
        <ManagedTable
            headers={businessOwnerHeaders}
            parameters={parameters}
            renderBody={(row) => (
                <RenderRowBusinessOwner
                    configuration={configuration}
                    row={row}
                    valueChange={valueChange}
                />
            )}
            renderBodyKey="id"
            renderFooter
            rowsApi="/tenantApp"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="application.name"
            toggles={togglesBusinessOwner({ t, text })}
        />
    );
};

const LifecycleStageTable = ({ configuration, valueChange }) => {
    const { t } = useTranslation();

    const { text } = configuration;

    const lifecycleStageHeaders = useMemo(
        () =>
            headers({
                field: LIFECYCLE_STAGE_CONFIG,
                t,
                text
            }),
        [t, text]
    );

    return (
        <ManagedTable
            headers={lifecycleStageHeaders}
            parameters={parameters}
            renderBody={(row) => (
                <RenderRowStatus
                    configuration={configuration}
                    row={row}
                    valueChange={valueChange}
                />
            )}
            renderBodyKey="id"
            renderFooter
            rowsApi="/tenantApp"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="application.name"
            toggles={togglesStatus({ t, text })}
        />
    );
};

const TagsTable = ({ configuration, valueChange }) => {
    const { t } = useTranslation();

    const { text } = configuration;

    const tagsHeaders = useMemo(
        () =>
            headers({
                field: TAGS_CONFIG,
                t,
                text
            }),
        [t, text]
    );

    return (
        <ManagedTable
            headers={tagsHeaders}
            parameters={parameters}
            renderBody={(row) => (
                <RenderRowTags
                    configuration={configuration}
                    row={row}
                    valueChange={valueChange}
                />
            )}
            renderBodyKey="id"
            renderFooter
            rowsApi="/tenantApp"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            searchFields={searchFields}
            sortDirection="ASC"
            sortFieldType="string"
            sortKey="application.name"
        />
    );
};

class BulkEditor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            deletedList: [],
            field: 'unselected',
            loaded: false
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { t } = this.props;
        const state = {};
        const params = Core.parseQueryString(window.location.search);

        state.loaded = true;
        state.configuration = {
            business_owner: {
                component: (
                    <BusinessOwnerPicker
                        multiple={false}
                        disableAllOption
                        unselectedOption={t(
                            'ManageApplicationAdmin.no_user_selected'
                        )}
                        unselectedValue="notAssigned"
                    />
                ),
                field: 'businessOwnerPlatformUserId',
                filteredValue: null,
                text: t('Common.business_owner')
            },
            status: {
                component: (
                    <LifecyclePicker multiple={false} disableAllOption />
                ),
                field: 'status',
                filteredValue: 'not_configured',
                text: t('Common.lifecycleStage')
            },
            // TODO: SAAS-1831 - Either remove this or re-implement it with Global Tags
            // tags: {
            //     component: (
            //         <TagPicker
            //             multiple
            //             disableAllOption
            //             unselectedOption="No Tags Selected"
            //             unselectedValue={['0']}
            //         />
            //     ),
            //     field: 'tags',
            //     filteredValue: [],
            //     text: t('Common.application_tag')
            // },
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            delete: {
                component: (
                    <IconButton color="secondary" size="small">
                        <DeleteOutlined />
                    </IconButton>
                ),
                componentRecovered: (
                    <IconButton color="secondary" size="small">
                        <RestoreFromTrash />
                    </IconButton>
                ),
                field: 'delete',
                filteredValue: null,
                text: `${t('Common.delete')} / ${t('Common.recover')}`
            }
        };

        state.field = Core.isset(state.configuration, params.field)
            ? params.field
            : 'unselected';
        this.setState(state);
    };

    valueChange = async (row, value) => {
        const { t, match } = this.props;
        const { clientId } = match.params;
        const { configuration, field, tenantLink } = this.state;

        try {
            if (field === 'tags') {
                const params = {
                    applicationId: row.applicationId,
                    applicationTags: value
                };
                if (clientId) {
                    params.platformTenantId = clientId;
                }

                try {
                    await axios.put('/tenantTag', params);
                } catch (e) {
                    debug.e('Error updating Tag', e);
                }
            } else if (field === 'business_owner') {
                const businessOwnerChanges =
                    BusinessOwnerFunctions.handleValues(value);
                this.setState({
                    tenantLink: {
                        ...tenantLink,
                        ...businessOwnerChanges
                    }
                });

                const params = {
                    ...businessOwnerChanges
                };
                if (clientId) {
                    params.platformTenantId = clientId;
                }

                try {
                    await axios.put(`/tenantApp/${row.id}`, params);
                } catch (e) {
                    debug.e('Error updating Owner', e);
                }
            } else {
                const params = {
                    [configuration[field].field]: value,
                    noBusinessOwnerRequired: false
                };
                if (clientId) {
                    params.platformTenantId = clientId;
                }

                try {
                    await axios.put(`/tenantApp/${row.id}`, params);
                } catch (e) {
                    debug.e('Update Error', params, e);
                }
            }
            Notify.success(t('Common.updated'));
        } catch (err) {
            Notify.error(t('Common.error'));
        }
    };

    render() {
        const { match, t } = this.props;
        const { clientId } = match.params;
        const { loaded, configuration, field } = this.state;

        if (!loaded) {
            return null;
        }

        const opts = [];

        Object.entries(configuration).forEach(([key, value]) => {
            opts.push({ text: value.text, value: key });
        });

        return (
            <AdminView page="configure">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <PageTitle title={t('Common.bulk_editor')} />
                        <AdminBackButton linkTo="/configure" />
                        <Paper elevation={0} variant="outlined">
                            <Grid item xs={12}>
                                {t('BulkEditor.description')}
                                <hr />
                                <Typography
                                    variant="span"
                                    color="textSecondary"
                                    fontWeight={600}
                                >
                                    {t('Common.field')}:{' '}
                                </Typography>
                                &nbsp;&nbsp;
                                <Select
                                    IconComponent={ExpandMore}
                                    variant="standard"
                                    value={field}
                                    onChange={(e) => {
                                        this.setState({
                                            field: e.target.value
                                        });
                                    }}
                                >
                                    <MenuItem value="unselected">
                                        {t('Common.selectAnOption')}
                                    </MenuItem>
                                    {opts.map((a) => (
                                        <MenuItem key={a.value} value={a.value}>
                                            {a.text}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            {field === BUSINESS_OWNER_CONFIG && (
                                <Grid item xs={12}>
                                    <BusinessOwnerTable
                                        configuration={
                                            configuration[BUSINESS_OWNER_CONFIG]
                                        }
                                        valueChange={this.valueChange}
                                    />
                                </Grid>
                            )}
                            {field === 'status' && (
                                <Grid item xs={12}>
                                    <LifecycleStageTable
                                        configuration={
                                            configuration[
                                                LIFECYCLE_STAGE_CONFIG
                                            ]
                                        }
                                        valueChange={this.valueChange}
                                    />
                                </Grid>
                            )}
                            {field === 'tags' && (
                                <Grid item xs={12}>
                                    <TagsTable
                                        configuration={
                                            configuration[TAGS_CONFIG]
                                        }
                                        valueChange={this.valueChange}
                                    />
                                </Grid>
                            )}
                            {field === 'delete' && (
                                <Grid item xs={12}>
                                    <ManagedTable
                                        headers={headersDelete({
                                            t,
                                            text: configuration[field].text
                                        })}
                                        parameters={parametersDelete}
                                        renderBody={(row) => {
                                            const {
                                                deletedList: stateDeletedList
                                            } = this.state;
                                            if (
                                                !stateDeletedList.includes(
                                                    row.applicationId
                                                )
                                            ) {
                                                return (
                                                    <Application
                                                        data={row.application}
                                                        nameHasLink
                                                    >
                                                        <TableCell>
                                                            <ApplicationNameLinkCell
                                                                nameNoTruncate
                                                                withCategory
                                                            />
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <UserBubbles
                                                                align="left"
                                                                max={5}
                                                                users={
                                                                    row.users
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <>
                                                                {row.removed ===
                                                                    true && (
                                                                    <>
                                                                        {React.cloneElement(
                                                                            configuration[
                                                                                field
                                                                            ]
                                                                                .componentRecovered,
                                                                            {
                                                                                onClick:
                                                                                    async () => {
                                                                                        try {
                                                                                            const params =
                                                                                                {
                                                                                                    removed: false
                                                                                                };
                                                                                            if (
                                                                                                clientId
                                                                                            ) {
                                                                                                params.platformTenantId =
                                                                                                    clientId;
                                                                                            }
                                                                                            await axios.put(
                                                                                                `/tenantApp/${row.id}`,
                                                                                                params
                                                                                            );
                                                                                            const {
                                                                                                deletedList
                                                                                            } =
                                                                                                this
                                                                                                    .state;
                                                                                            deletedList.push(
                                                                                                row.applicationId
                                                                                            );
                                                                                            this.setState(
                                                                                                {
                                                                                                    deletedList
                                                                                                }
                                                                                            );
                                                                                            Notify.success(
                                                                                                t(
                                                                                                    'Classify.recovered'
                                                                                                )
                                                                                            );
                                                                                        } catch (e) {
                                                                                            Notify.error(
                                                                                                t(
                                                                                                    'Classify.recovered_error'
                                                                                                )
                                                                                            );
                                                                                        }
                                                                                    }
                                                                            }
                                                                        )}
                                                                    </>
                                                                )}
                                                                {row.removed !==
                                                                    true && (
                                                                    <>
                                                                        {React.cloneElement(
                                                                            configuration[
                                                                                field
                                                                            ]
                                                                                .component,
                                                                            {
                                                                                onClick:
                                                                                    async () => {
                                                                                        try {
                                                                                            const params =
                                                                                                {
                                                                                                    classified: true,
                                                                                                    removed: true
                                                                                                };
                                                                                            if (
                                                                                                clientId
                                                                                            ) {
                                                                                                params.platformTenantId =
                                                                                                    clientId;
                                                                                            }
                                                                                            await axios.put(
                                                                                                `/tenantApp/${row.id}`,
                                                                                                params
                                                                                            );
                                                                                            const {
                                                                                                deletedList
                                                                                            } =
                                                                                                this
                                                                                                    .state;
                                                                                            deletedList.push(
                                                                                                row.applicationId
                                                                                            );
                                                                                            this.setState(
                                                                                                {
                                                                                                    deletedList
                                                                                                }
                                                                                            );
                                                                                            Notify.success(
                                                                                                t(
                                                                                                    'Classify.removed'
                                                                                                )
                                                                                            );
                                                                                        } catch (e) {
                                                                                            Notify.error(
                                                                                                t(
                                                                                                    'Classify.removed_error'
                                                                                                )
                                                                                            );
                                                                                        }
                                                                                    }
                                                                            }
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        </TableCell>
                                                    </Application>
                                                );
                                            }
                                            return null;
                                        }}
                                        renderBodyKey="id"
                                        renderFooter
                                        rowsApi="/tenantApp"
                                        rowsApiPreload
                                        rowsPerPage={10}
                                        searchEnabled
                                        searchFields={searchFields}
                                        sortDirection="ASC"
                                        sortFieldType="string"
                                        sortKey="application.name"
                                        toggles={togglesDelete({ t })}
                                    />
                                </Grid>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </AdminView>
        );
    }
}
export default withRouter(withTranslation()(BulkEditor));
